import React, { useState, useRef, useEffect } from 'react';
import './RealtimeVoice.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const WS_URL = BACKEND_URL.replace('http', 'ws');

const RealtimeVoice = ({ user, session, context, onTranscript, onClose, onAudioReceived, onCreditDeducted }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioStreamRef = useRef(null);
  const processorRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const isListeningRef = useRef(false);  // Ref for closure issue
  const lastAudioChunksRef = useRef([]);  // Store last AI response audio for replay

  // Auto-connect on mount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, []);

  const connect = async () => {
    try {
      setError(null);
      
      // WebSocket bağlantısı
      const ws = new WebSocket(`${WS_URL}/api/realtime/voice`);
      wsRef.current = ws;

      ws.onopen = async () => {
        console.log('✅ WebSocket connected');
        
        // İlk mesaj: user_id, session_id, context
        ws.send(JSON.stringify({
          user_id: user.id,
          session_id: session.id,
          context: context
        }));
        
        setIsConnected(true);
        
        // Mikrofon başlat
        await startMicrophone();
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        handleOpenAIMessage(message);
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        // Don't show error to user if connection works
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket closed', event.code, event.reason);
        setIsConnected(false);
        stopMicrophone();
      };

    } catch (err) {
      console.error('Connection error:', err);
      // Don't show error if system works
    }
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    stopMicrophone();
    setIsConnected(false);
  };

  const startMicrophone = async () => {
    try {
      // Audio context oluştur
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 24000
      });
      audioContextRef.current = audioContext;

      // Mikrofon stream
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 24000,
          echoCancellation: true,
          noiseSuppression: true
        } 
      });
      audioStreamRef.current = stream;

      // Audio processor
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        // WebSocket kontrolü
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          return;
        }
        
        // Mikrofon kapalıysa ses gönderme (Zeha konuşuyorken)
        if (!isListeningRef.current) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);
        
        // Float32 → Int16 PCM
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        // Base64 encode ve gönder
        const base64Audio = btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)));
        
        try {
          wsRef.current.send(JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: base64Audio
          }));
        } catch (error) {
          console.error('❌ Error sending audio:', error);
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      setIsListening(true);
      isListeningRef.current = true;  // Set ref too!
      console.log('🎤 Microphone started');

    } catch (err) {
      console.error('Microphone error:', err);
      setError('Mikrofon erişimi reddedildi');
    }
  };

  const stopMicrophone = () => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsListening(false);
    isListeningRef.current = false;  // Set ref too!
    console.log('🔇 Microphone stopped');
  };

  const handleOpenAIMessage = (message) => {
    const type = message.type;
    
    // Log all events for debugging
    if (!['response.audio.delta', 'input_audio_buffer.speech_started', 'input_audio_buffer.speech_stopped'].includes(type)) {
      console.log('📨 OpenAI event:', type, message);
    }

    switch (type) {
      case 'session.created':
        console.log('✅ Session created');
        break;

      case 'session.updated':
        console.log('✅ Session updated');
        break;

      case 'input_audio_buffer.speech_started':
        // Kullanıcı konuşmaya başladı
        console.log('🎤 User started speaking');
        
        // Eğer AI konuşuyorsa, kes!
        if (isSpeaking && wsRef.current) {
          wsRef.current.send(JSON.stringify({
            type: 'response.cancel'
          }));
          console.log('✋ Interrupted AI response');
        }
        break;

      case 'input_audio_buffer.speech_stopped':
        // Kullanıcı konuşmayı bitirdi
        console.log('🎤 User stopped speaking');
        // OpenAI VAD otomatik olarak commit ve response oluşturacak (turn_detection.create_response: true)
        break;

      case 'conversation.item.created':
        // Yeni konuşma öğesi
        const item = message.item;
        
        if (item.type === 'message' && item.role === 'user') {
          // Kullanıcı mesajı - transcript varsa chat'e ekle
          const content = item.content?.[0];
          if (content?.type === 'input_audio' && content.transcript) {
            if (onTranscript) {
              onTranscript({ 
                role: 'user', 
                text: content.transcript,
                partial: false 
              });
            }
          }
        }
        break;

      case 'conversation.item.input_audio_transcription.completed':
        // Kullanıcı konuşması transkript edildi
        const transcript = message.transcript;
        console.log('📝 User transcript:', transcript);
        if (transcript && onTranscript) {
          onTranscript({ 
            role: 'user', 
            text: transcript,
            partial: false 
          });
        }
        break;

      case 'response.output_item.done':
        // Response item tamamlandı - transcript burada
        const outputItem = message.item;
        if (outputItem && outputItem.type === 'message') {
          const content = outputItem.content;
          if (content && content.length > 0) {
            // Audio transcript varsa (öncelikli)
            const audioContent = content.find(c => c.type === 'audio');
            if (audioContent && audioContent.transcript && onTranscript) {
              console.log('📝 Assistant transcript:', audioContent.transcript);
              onTranscript({
                role: 'assistant',
                text: audioContent.transcript,
                partial: false
              });
            } else {
              // Text content varsa (fallback)
              const textContent = content.find(c => c.type === 'text');
              if (textContent && textContent.text && onTranscript) {
                console.log('📝 Assistant transcript:', textContent.text);
                onTranscript({
                  role: 'assistant',
                  text: textContent.text,
                  partial: false
                });
              }
            }
          }
        }
        break;

      case 'response.audio_transcript.delta':
        // AI'ın konuşması transkript ediliyor (kısmi) - IGNORE, use output_item.done instead
        break;

      case 'response.audio_transcript.done':
        // AI'ın konuşması transkript edildi (tam) - IGNORE, use output_item.done instead
        break;

      case 'response.audio.delta':
        // Ses verisi geldi
        const chunkLength = message.delta?.length || 0;
        if (chunkLength > 0) {
          console.log('🔊 Audio chunk received, length:', chunkLength, 'queue size:', audioQueueRef.current.length);
          // Store audio chunk for replay
          lastAudioChunksRef.current.push(message.delta);
          // Play immediately
          playAudioChunk(message.delta);
          setIsSpeaking(true);
        }
        break;

      case 'response.audio.done':
        // Ses tamamlandı - ama queue'daki chunk'lar hala çalınıyor olabilir
        console.log('🎵 Audio streaming done, waiting for queue to finish...');
        
        // Queue boşalana kadar bekle
        const waitForQueue = () => {
          if (audioQueueRef.current.length > 0 || isPlayingRef.current) {
            console.log('⏳ Waiting for audio queue, remaining:', audioQueueRef.current.length);
            setTimeout(waitForQueue, 200);
          } else {
            console.log('✅ Audio playback fully completed');
            setIsSpeaking(false);
            
            // CRITICAL: Mikrofonu tekrar aç - Zeha konuşmayı bitirdi
            console.log('🎤 Resuming microphone - AI finished speaking');
            isListeningRef.current = true;
            setIsListening(true);
            
            // Send audio chunks to parent for replay functionality
            if (lastAudioChunksRef.current.length > 0 && onAudioReceived) {
              onAudioReceived(lastAudioChunksRef.current);
            }
          }
        };
        
        waitForQueue();
        break;

      case 'response.created':
        console.log('🎬 Response created:', JSON.stringify(message, null, 2));
        // Clear previous audio chunks for new response
        lastAudioChunksRef.current = [];
        
        // CRITICAL: Mikrofonu kapat - Zeha konuşacak
        console.log('🔇 Pausing microphone - AI is speaking');
        if (processorRef.current) {
          // Mikrofonu geçici olarak devre dışı bırak
          isListeningRef.current = false;
          setIsListening(false);
        }
        
        // 💰 Kredi düşümünü tetikle - Zeha cevap vermeye başladı
        if (onCreditDeducted) {
          onCreditDeducted();
        }
        break;

      case 'response.done':
        console.log('✅ Response completed');
        console.log('📋 Response object:', message.response);
        console.log('📋 Response output:', message.response?.output);
        console.log('📋 Full response.done:', JSON.stringify(message, null, 2));
        setIsSpeaking(false);
        
        // Fallback: Mikrofonu tekrar aç (eğer audio.done gelmezse)
        if (!isListeningRef.current) {
          console.log('🎤 Fallback: Resuming microphone after response.done');
          isListeningRef.current = true;
          setIsListening(true);
        }
        break;

      case 'error':
        console.error('❌ OpenAI error:', message.error);
        // Ignore unknown_parameter errors (they don't affect functionality)
        const errorCode = message.error?.code;
        if (errorCode !== 'unknown_parameter') {
          setError(message.error.message || 'Bir hata oluştu');
        }
        break;

      default:
        // Diğer event'ler
        break;
    }
  };

  const playAudioChunk = async (base64Audio) => {
    try {
      if (!base64Audio) {
        console.warn('⚠️ Empty audio chunk received');
        return;
      }

      // AudioContext oluştur veya yeniden oluştur
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        console.log('🎵 Creating new AudioContext...');
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 24000
        });
        console.log('🎵 AudioContext created, state:', audioContextRef.current.state);
      }

      // Resume AudioContext if suspended (browser policy)
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
        console.log('▶️ AudioContext resumed');
      }

      // Base64 → ArrayBuffer
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Int16 PCM → Float32
      const int16Array = new Int16Array(bytes.buffer);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768.0;
      }

      // AudioBuffer oluştur
      const audioBuffer = audioContextRef.current.createBuffer(
        1, // mono
        float32Array.length,
        24000 // sample rate
      );
      audioBuffer.getChannelData(0).set(float32Array);

      // Queue'ya ekle
      audioQueueRef.current.push(audioBuffer);
      console.log('📥 Audio chunk queued, queue length:', audioQueueRef.current.length, 'isPlaying:', isPlayingRef.current);

      // Eğer çalmıyorsa, başlat
      if (!isPlayingRef.current) {
        console.log('🎬 Starting playback...');
        playNextChunk();
      }
    } catch (error) {
      console.error('❌ Audio chunk processing error:', error);
      // Don't stop playback on error, just skip this chunk
    }
  };

  const playNextChunk = async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      console.log('🔇 Audio queue empty, playback stopped');
      return;
    }

    // AudioContext kontrolü ve yeniden oluşturma
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      console.warn('⚠️ AudioContext is null or closed, recreating...');
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 24000
        });
        console.log('🎵 AudioContext recreated');
      } catch (err) {
        console.error('❌ Failed to create AudioContext:', err);
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        return;
      }
    }

    // Resume if suspended
    if (audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        console.log('▶️ AudioContext resumed for playback');
      } catch (err) {
        console.error('Failed to resume AudioContext:', err);
      }
    }

    isPlayingRef.current = true;
    const audioBuffer = audioQueueRef.current.shift();
    
    if (!audioBuffer) {
      console.warn('⚠️ Empty audio buffer in queue');
      if (audioQueueRef.current.length > 0) {
        setTimeout(() => playNextChunk(), 10);
      } else {
        isPlayingRef.current = false;
      }
      return;
    }

    try {
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);

      source.onended = () => {
        console.log('✅ Audio chunk played, remaining:', audioQueueRef.current.length);
        // CRITICAL: Continue immediately if queue has more
        if (audioQueueRef.current.length > 0) {
          playNextChunk();
        } else {
          isPlayingRef.current = false;
          console.log('🏁 All audio chunks played');
        }
      };

      // Add error handler
      source.onerror = (error) => {
        console.error('❌ Audio source error:', error);
        // Try to play next chunk
        if (audioQueueRef.current.length > 0) {
          console.log('🔄 Retrying with next chunk...');
          setTimeout(() => playNextChunk(), 50);
        } else {
          isPlayingRef.current = false;
        }
      };

      console.log('▶️ Playing audio chunk, duration:', audioBuffer.duration.toFixed(2), 's, queue remaining:', audioQueueRef.current.length);
      source.start();
    } catch (error) {
      console.error('❌ Audio playback error:', error);
      // Don't stop - try to play next chunk after a short delay
      if (audioQueueRef.current.length > 0) {
        console.log('🔄 Error occurred, trying next chunk...');
        isPlayingRef.current = false;
        setTimeout(() => playNextChunk(), 100);
      } else {
        isPlayingRef.current = false;
      }
    }
  };

  return (
    <div className="realtime-voice-container">
      {isConnected ? (
        <div className="voice-active-compact">
          <div className={`voice-indicator-compact ${isListening ? 'listening' : ''} ${isSpeaking ? 'speaking' : ''}`}>
            <div className="voice-wave-compact"></div>
            <div className="voice-wave-compact"></div>
            <div className="voice-wave-compact"></div>
          </div>
          
          <span className="voice-status-compact">
            {isSpeaking ? '🗣️' : isListening ? '🎤' : '⏸️'}
          </span>

          <button 
            className="voice-end-btn"
            onClick={() => {
              disconnect();
              if (onClose) onClose();
            }}
            title="Sesli konuşmayı sonlandır"
          >
            ✕
          </button>
        </div>
      ) : (
        <div className="voice-connecting">
          <div className="connecting-spinner"></div>
          <span>Bağlanıyor...</span>
        </div>
      )}

      {error && (
        <div className="voice-error-compact">
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default RealtimeVoice;
